import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useChartColors } from '../../hooks';
import './StatusDonut.css';

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  color: string;   // resolved hex/rgb (theme-aware handled by caller)
}

interface StatusDonutProps {
  segments: DonutSegment[];
  total: number;
  centerLabel?: string;
}

const fmtPct = (value: number, total: number): string => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
};

interface TooltipPayload { payload: DonutSegment; }

const DonutTooltip: React.FC<{ active?: boolean; payload?: TooltipPayload[]; total: number }> = ({ active, payload, total }) => {
  if (!active || !payload || payload.length === 0) return null;
  const seg = payload[0].payload;
  return (
    <div className="donut-tooltip">
      <span className="donut-tooltip-dot" style={{ background: seg.color }} />
      <span className="donut-tooltip-label">{seg.label}</span>
      <strong>{seg.value}</strong>
      <em>{fmtPct(seg.value, total)}</em>
    </div>
  );
};

export const StatusDonut: React.FC<StatusDonutProps> = ({ segments, total, centerLabel = 'Total' }) => {
  const colors = useChartColors();
  const data = segments.filter(s => s.value > 0);

  return (
    <div className="donut-panel">
      <div className="donut-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="100%"
              paddingAngle={data.length > 1 ? 2 : 0}
              stroke={colors.surface}
              strokeWidth={2}
              startAngle={90}
              endAngle={-270}
              isAnimationActive
            >
              {data.map(seg => <Cell key={seg.key} fill={seg.color} />)}
            </Pie>
            <Tooltip content={<DonutTooltip total={total} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="donut-center" aria-hidden="true">
          <strong>{total}</strong>
          <span>{centerLabel}</span>
        </div>
      </div>
      <ul className="donut-legend">
        {data.map(seg => (
          <li key={seg.key} className="donut-legend-row">
            <span className="donut-legend-dot" style={{ background: seg.color }} />
            <span className="donut-legend-label">{seg.label}</span>
            <strong>{seg.value}</strong>
            <em>{fmtPct(seg.value, total)}</em>
          </li>
        ))}
      </ul>
    </div>
  );
};
